import useContentContext from './useContentContext'

import type {
  Kinerja,
  KinerjaPraktikPembelajaranContentIndikator,
  KinerjaSelection,
  KinerjaUtamaDetail,
  PerilakuKerjaPayload,
} from 'types/kinerja'

interface KinerjaChanges<T = KinerjaUtamaDetail> {
  originalNumber: number
  originalValue: KinerjaSelection
  originalKinerja?: T
  kinerja?: T
}

const useSKP = (skp: Kinerja) => {
  const {
    getPraktikPembelajaranById,
    getPengembanganKompetensiById,
    getTugasTambahanById,
    getAspekPerilakuKerjaById,
  } = useContentContext()

  const isEmptyTugasTambahan =
    !skp?.reviewTugasTambahan.length || // This means guru didn't choose any tugas tambahan
    (skp?.reviewTugasTambahan.length === 1 &&
      skp?.reviewTugasTambahan[0].id === 0) // This means KS deleted guru's tugas tambahan

  const getChangesPraktikPembelajaran = (
    newValue: number = skp?.reviewPraktikPembelajaran
  ): KinerjaChanges<KinerjaPraktikPembelajaranContentIndikator> => {
    const originalPraktikPembelajaran = getPraktikPembelajaranById(
      skp?.praktikPembelajaran
    )
    const newPraktikPembelajaran = getPraktikPembelajaranById(newValue)

    return {
      originalNumber: 1,
      originalValue: { id: skp?.praktikPembelajaran, kuantitas: 1 },
      originalKinerja: originalPraktikPembelajaran,
      kinerja: newPraktikPembelajaran,
    }
  }

  /**
   * Returns comparable kinerja values at current index. Data will be compared from skp.pengembanganKompetensi
   *
   * 1. If pengembanganKompetensi with the same id is found on skp.pengembanganKompetensi, return it as originalKinerja
   * 2. If pengembanganKompetensi with the same id is not found on skp.pengembanganKompetensi, return skp.pengembanganKompetensi at the same index as originalKinerja
   * 3. If skp.pengembanganKompetensi at the same index is found in skp.reviewPengembanganKompetensi, return empty values as originalKinerja
   *
   * @param {number} index - Pengembangan Kompetensi index to get the comparable values
   * @param {KinerjaSelection[]} newValues - Defaults to skp.reviewPengembanganKompetensi. Useful for detecting live changes from user input
   * @param {KinerjaSelection[]} originalValues - Defaults to skp.pengembanganKompetensi
   */
  const getChangesPengembanganKompetensiByIndex = (
    index: number,
    newValues: KinerjaSelection[] = skp?.reviewPengembanganKompetensi ?? [],
    originalValues: KinerjaSelection[] = skp?.pengembanganKompetensi ?? []
  ): KinerjaChanges => {
    const newPengembanganKompetensi = newValues[index]

    // Get original Pengembangan Kompetensi at the same index
    const valueAtIndex = originalValues[index]
    const kinerjaAtIndex = getPengembanganKompetensiById(valueAtIndex?.id)
    const isValueAtIndexDeleted = !newValues.find(
      (kinerja) => kinerja.id === valueAtIndex?.id
    )

    // Get pengembanganKompetensi's original index on originalValues
    const originalIndex = originalValues.findIndex(
      (kinerja) => kinerja.id === newPengembanganKompetensi?.id
    )

    const originalValue =
      originalValues[originalIndex] ||
      (isValueAtIndexDeleted ? valueAtIndex : null)
    const originalKinerja = getPengembanganKompetensiById(originalValue?.id)
    const kinerja =
      originalValue?.id === newPengembanganKompetensi?.id
        ? originalKinerja
        : getPengembanganKompetensiById(newPengembanganKompetensi?.id)

    return {
      originalNumber: originalIndex < 0 ? index + 1 : originalIndex + 1,
      originalValue,
      originalKinerja:
        !originalValue && isValueAtIndexDeleted
          ? kinerjaAtIndex
          : originalKinerja
          ? {
              ...originalKinerja,
              targetKuantitas: `${originalValue.kuantitas} kegiatan`,
              bobot: originalKinerja?.bobot * originalValue.kuantitas,
            }
          : null,
      kinerja: {
        ...kinerja,
        targetKuantitas: `${newPengembanganKompetensi?.kuantitas} kegiatan`,
        bobot: kinerja?.bobot * newPengembanganKompetensi?.kuantitas,
      },
    }
  }

  /**
   * Returns comparable kinerja values at current index. Data will be compared from skp.tugasTambahan
   *
   * 1. If tugasTambahan with the same id is found on skp.tugasTambahan, return it as originalKinerja
   * 2. If tugasTambahan with the same id is not found on skp.tugasTambahan, return skp.tugasTambahan at the same index as originalKinerja
   * 3. If skp.tugasTambahan at the same index is found in skp.reviewTugasTambahan, return empty values as originalKinerja
   *
   * @param {number} index - Pengembangan Kompetensi index to get the comparable values
   * @param {KinerjaSelection[]} newValues - Defaults to skp.reviewTugasTambahan. Useful for detecting live changes from user input
   * @param {KinerjaSelection[]} originalValues - Defaults to skp.tugasTambahan
   */

  const getChangesTugasTambahanByIndex = (
    index: number,
    newValues: KinerjaSelection[] = skp?.reviewTugasTambahan ?? [],
    originalValues: KinerjaSelection[] = skp?.tugasTambahan ?? []
  ): KinerjaChanges => {
    const newTugasTambahan = newValues[index]

    // Get original Tugas Tambahan at the same index
    const valueAtIndex = originalValues[index]
    const kinerjaAtIndex = getTugasTambahanById(valueAtIndex?.id)
    const isValueAtIndexDeleted = !newValues.find(
      (kinerja) => kinerja.id === valueAtIndex?.id
    )

    // Get tugasTambahan's original index on originalValues
    const originalIndex = originalValues.findIndex(
      (kinerja) => kinerja.id === newTugasTambahan.id
    )

    const originalValue =
      originalValues[originalIndex] ||
      (isValueAtIndexDeleted ? valueAtIndex : null)
    const originalKinerja = getTugasTambahanById(originalValue?.id)
    const kinerja =
      originalValue?.id === newTugasTambahan?.id
        ? originalKinerja
        : getTugasTambahanById(newTugasTambahan.id)

    return {
      originalNumber: originalIndex < 0 ? index + 1 : originalIndex + 1,
      originalValue,
      originalKinerja:
        !originalValue && isValueAtIndexDeleted
          ? kinerjaAtIndex
          : originalKinerja,
      kinerja,
    }
  }

  const getChangesPerilakuKerja = (perilakuKerja: PerilakuKerjaPayload) => {
    const aspek = getAspekPerilakuKerjaById(perilakuKerja.aspekId)

    const originalValue = skp?.prilakuKerja.find(
      (original) => original.aspekId === perilakuKerja.aspekId
    )
    const originalIndikator = aspek?.indikator.find(
      (indikator) => indikator.id === originalValue.indikatorId
    )
    const indikator = aspek?.indikator.find(
      (indikator) => indikator.id === perilakuKerja.indikatorId
    )
    const originalPerwujudanPerilaku = originalIndikator?.ekspektasi.find(
      (ekspektasi) => ekspektasi.id === originalValue.ekspektasiId
    )
    const perwujudanPerilaku = indikator?.ekspektasi.find(
      (ekspektasi) => ekspektasi.id === perilakuKerja.ekspektasiId
    )

    return {
      aspek,
      originalIndikator: originalIndikator?.value,
      indikator: indikator?.value,
      originalPerwujudanPerilaku: originalPerwujudanPerilaku?.value,
      perwujudanPerilaku: perwujudanPerilaku?.value,
    }
  }

  return {
    isEmptyTugasTambahan,
    getChangesPraktikPembelajaran,
    getChangesPengembanganKompetensiByIndex,
    getChangesTugasTambahanByIndex,
    getChangesPerilakuKerja,
  }
}

export default useSKP
